import { TitleCasePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Optional,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SortTable } from '@rivethealth/data/analytics/account-analytics-events';
import { sentenceCaseFromCamelCase } from '@rivethealth/data/capitalization';
import { Option } from '@rivethealth/data/options';
import { ColumnSettings } from '@rivethealth/data/saved-view';
import {
  Filter,
  Order,
  Result,
  SuperColumn,
} from '@rivethealth/data/super-table';
import { ObservableInput } from 'observable-input';
import {
  BehaviorSubject,
  combineLatest,
  from,
  merge,
  Observable,
  Subject,
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountAnalyticsService } from '../../app/account/account-analytics.service';
import { ChangeService } from '../core/change.service';
import { replayLast } from '../rx/util';
import {
  CHECKBOX_PX,
  SuperTableContext,
  SUPER_TABLE_CONTEXT_TOKEN,
} from './super-table';
import { SuperTableHeaderComponent } from './super-table-header.component';
import { SuperTableRowDirective } from './super-table-row.directive';
import { SuperTableSelectable } from './super-table-selectable';
import { SuperTableStickyDirective } from './super-table-sticky.directive';
import { TableColumnStore } from './table-column-store.service';

export interface ViewState {
  sortDisabled: boolean;
  page: number;
  pageSize: number;
}

export namespace ViewState {
  export function equals(a: ViewState, b: ViewState) {
    return a.page === b.page && a.pageSize === b.pageSize;
  }
}

interface ScrollPosition {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

const ES_MAX_RECORD_COUNT = 10_000;

@Component({
  host: {
    class: 'rv-grid small',
  },
  providers: [
    { provide: SUPER_TABLE_CONTEXT_TOKEN, useExisting: SuperTableComponent },
    {
      provide: SuperTableSelectable,
      useExisting: forwardRef(() => SuperTableComponent),
    },
    TitleCasePipe,
    ChangeService,
  ],
  selector: 'super-table',
  templateUrl: './super-table.component.html',
  styleUrls: ['./super-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperTableComponent<T = unknown>
  implements AfterViewInit, SuperTableSelectable<T>, SuperTableContext
{
  @ViewChild('columnSelect')
  columnSelect: TemplateRef<{}>;

  @ViewChildren(SuperTableHeaderComponent)
  headers!: SuperTableHeaderComponent[];

  @ContentChild(SuperTableRowDirective, { static: false })
  rowDirective: SuperTableRowDirective<T>;

  @ViewChild('scroller', { static: false }) scroller: ElementRef;

  @ContentChildren(SuperTableHeaderComponent, { descendants: true })
  superTableHeaderComponents!: QueryList<SuperTableHeaderComponent>;

  @ContentChildren(SuperTableStickyDirective)
  superTableStickyCells: QueryList<SuperTableStickyDirective>;

  @Input()
  columnHeaders: SuperColumn[] = [];

  @Input()
  parentEnabledColumns?: SuperColumn[];

  // TODO This is possibly unnecessary, and is something to look into for other refactors.
  @Input()
  customizeIndexesThrough?: number;

  @Input()
  emptyAlternate = false;

  @Input()
  emptyText = 'No records.';

  @Input()
  flatTop = false;

  @Input()
  groupStyling = false;

  @Input()
  overrideDefaultDeselection: boolean = false;
  // Use this optional input if you want to build your own
  // deselection behavior

  @Input()
  hasCheckbox: boolean = false;

  @Input()
  pageSizes: number[] = [25, 50, 100, 250];

  get pageSizeOptions(): Option[] {
    return this.pageSizes.map(size => ({
      id: String(size),
      title: String(size),
      subtitle: null,
    }));
  }

  @Input()
  maxHeight: number | null = null;

  @Input()
  name: string = '';

  @Input()
  noColumnSettings: boolean = false;

  @Input()
  noShadow = false;

  @Input()
  noBorder = false;

  @Input()
  scrollable = true;

  @Input()
  @Output()
  @ObservableInput()
  data!: Observable<Result<T>>;

  @Input()
  @ObservableInput()
  filter!: Observable<Filter<any>>;

  @Input()
  @ObservableInput()
  rowsSelected!: Observable<T[]>;

  @Input()
  @ObservableInput()
  selectionKey!: Observable<keyof T>;

  @Input()
  @ObservableInput()
  sort!: Observable<Order>;

  @Input('trackBy')
  @ObservableInput()
  _trackBy!: Observable<(t: T) => any>;
  trackBy = this._trackBy.pipe(startWith((t: T) => t));

  @Input()
  @ObservableInput()
  view!: Observable<ViewState>;

  @Input()
  totalText: string | null = null;

  readonly changeSort = new Subject<Order>();

  readonly changeView = new Subject<Partial<ViewState>>();

  readonly doSelect = new Subject<{ value: T; selected: boolean } | null>();

  dragEvent = new Subject<[number, number]>();

  fireSelectionChange = new Subject<void>();

  readonly refreshPersistedColumns = new BehaviorSubject(undefined);

  scrollPosition = new Subject<ScrollPosition>();

  selectedColumns = new Subject<string[]>();
  columnsSelected: boolean = true;
  columnsLoaded: boolean = false;

  columnDropdownOptions: Option[];

  readonly effectiveSort: Observable<Order | null> = combineLatest([
    this.view.pipe(
      map(v => v.sortDisabled),
      startWith(false),
    ),
    this.sort,
  ]).pipe(map(([sortDisabled, sort]) => (sortDisabled ? null : sort)));

  persistedColumns: Observable<ColumnSettings[]> =
    this.refreshPersistedColumns.pipe(
      switchMap(() => from(this.tableColumnStore.get(this.name))),
      replayLast(),
    );

  persistedColumnsSelected: Observable<string[]> = this.persistedColumns.pipe(
    map(columns => columns.filter(c => c.enabled).map(c => c.name)),
  );

  readonly pageCount = combineLatest([this.data, this.view]).pipe(
    map(([data, { pageSize }]) =>
      data && data.noRange
        ? Math.ceil(
            Math.min(data.noRange.count, ES_MAX_RECORD_COUNT) / pageSize,
          )
        : 1,
    ),
  );

  olddata: Result<T>;
  readonly isLoading = combineLatest([this.data]).pipe(
    map(([data]) => {
      if (data && data.data) {
        this.olddata = data;
        return false;
      }
      return true;
    }),
  );

  private readonly rowsSelectedMap = combineLatest([
    this.selectionKey,
    this.rowsSelected.pipe(distinctUntilChanged()),
  ]).pipe(
    map(([selectionKey, rows]) => {
      return new Map(rows.map(row => [row[selectionKey], row]));
    }),
    replayLast(),
  );

  emitEnabledArrays() {
    this.enabledColumnArray.emit(
      this.columnHeaders.filter(column => column.enabled),
    );

    this.enabledPinnedArray.emit(
      this.columnHeaders.filter(column => column.enabled && column.pinned),
    );

    this.enabledUnpinnedArray.emit(
      this.columnHeaders.filter(column => column.enabled && !column.pinned),
    );
  }

  async handleDragEvent(
    [previousIndex, currentIndex]: [number, number],
    correctHidden = true,
  ) {
    let counter = 0;
    let foundCurrent = false;
    let foundPrev = false;
    if (correctHidden) {
      this.columnHeaders.forEach((e, i) => {
        if (counter === previousIndex && e.enabled && !foundPrev) {
          previousIndex = i;
          counter++;
          foundPrev = true;
        } else if (counter === currentIndex && e.enabled && !foundCurrent) {
          currentIndex = i;
          counter++;
          foundCurrent = true;
        } else if (e.enabled) {
          counter++;
        }
      });
    }

    if (currentIndex < 0 || currentIndex >= this.columnHeaders.length) return;

    await this.tableColumnStore.moveColumnToPosition(
      this.name,
      previousIndex,
      currentIndex,
    );

    await this.updateOrSetColumnDropdownOptions();

    await this.updateColumnHeadersFromSettings();

    this.setStickyValues();

    this.refreshPersistedColumns.next(undefined);

    this.emitEnabledArrays();
  }

  async handlePinnedEvent([id, pinned]: [string, boolean]) {
    await this.tableColumnStore.setPinned(this.name, id, pinned);

    await this.wasResized(id);
    await this.updateOrSetColumnDropdownOptions();

    this.refreshPersistedColumns.next(undefined);
    await this.handleDragEvent([0, 0]);
  }

  onScroll(evt: Event) {
    this.scrollPosition.next(<HTMLElement>evt.target);
  }

  private removeData(selected: Map<T[keyof T], T>, data: T[], key: keyof T) {
    data.forEach(x => selected.delete(x[key]));
    return Array.from(selected.values());
  }

  select(value: T | null, selected: boolean) {
    this.doSelect.next({ value: value!, selected });
  }

  selected(value: T | null) {
    if (value == null) {
      return combineLatest([
        this.data,
        this.selectionKey,
        this.rowsSelectedMap,
      ]).pipe(
        map(([data, selectionKey, selected]) =>
          data && data.data.length > 0
            ? data.data
                .filter(x => {
                  if (!(selectionKey == 'id' && +x[selectionKey] < 0)) {
                    return x;
                  }
                })
                .every(value => selected.has(value[selectionKey]))
            : false,
        ),
      );
    }
    return combineLatest([this.selectionKey, this.rowsSelectedMap]).pipe(
      map(([selectionKey, selected]) => selected.has(value[selectionKey])),
    );
  }

  setStickyValues() {
    let lastWidth: number = 0;
    if (this.columnHeaders.length > 0) {
      this.columnHeaders[0].sticky = this.hasCheckbox ? CHECKBOX_PX : 0;
      if (this.columnHeaders[0].enabled) {
        lastWidth = this.columnHeaders[0].currentWidth || 0;
      }
      for (let i = 1; i < this.columnHeaders.length; i++) {
        if (this.columnHeaders[i].enabled) {
          this.columnHeaders[i].sticky =
            (this.columnHeaders[i - 1].sticky || 0) + lastWidth;
          lastWidth = this.columnHeaders[i].currentWidth || 0;
        } else {
          this.columnHeaders[i].sticky = this.columnHeaders[i - 1].sticky || 0;
        }
      }
    }
  }

  sortColumnHeaders() {
    this.columnHeaders = this.columnHeaders.sort((a, b) => {
      if (a.ordinal !== null && b.ordinal !== null) {
        return a.ordinal - b.ordinal;
      }
      return 0;
    });
  }

  async updateColumnHeadersFromSettings() {
    if (!this.noColumnSettings) {
      const settings = await this.tableColumnStore.get(this.name);

      this.columnHeaders.forEach(col => {
        const matchingSetting: ColumnSettings | undefined = settings.find(
          setting =>
            setting.name.toLowerCase() === col.columnTitle.toLowerCase(),
        );

        col.ordinal = matchingSetting ? matchingSetting.ordinal : col.ordinal;
        col.pinned = matchingSetting ? matchingSetting.pinned : col.pinned;
        col.currentWidth = matchingSetting
          ? matchingSetting.width
          : col.currentWidth;
        col.enabled = matchingSetting ? matchingSetting.enabled : col.enabled;
      });
    }
    this.sortColumnHeaders();
  }

  async updateOrSetColumnDropdownOptions() {
    const storedColumns: ColumnSettings[] = (
      await this.tableColumnStore.get(this.name)
    ).filter(c => c.name != null);
    if (this.columnHeaders) {
      if (storedColumns.length === 0) {
        this.columnDropdownOptions = this.columnHeaders
          .filter(c => c.columnTitle != null)
          .map(c => ({
            id: c.columnTitle.toLowerCase(),
            title: c.columnTitle,
            subtitle: '',
          }));
      } else {
        this.columnDropdownOptions = storedColumns
          .filter(
            storedColumn =>
              this.columnHeaders.find(
                columnHeader =>
                  columnHeader.columnTitle.toLowerCase() ==
                  storedColumn.name.toLowerCase(),
              ) != undefined,
          )
          .map(c => ({
            id: c.name.toLowerCase(),
            title:
              this.columnHeaders.find(
                h => h.columnTitle.toLowerCase() === c.name,
              )?.columnTitle || c.name,
            subtitle: '',
          }));
      }

      if (this.customizeIndexesThrough) {
        this.columnDropdownOptions = this.columnDropdownOptions.slice(
          0,
          this.customizeIndexesThrough,
        );
      }
    }
  }

  async wasResized(columnName: string) {
    await this.updateColumnHeadersFromSettings();

    const foundIndex = this.columnHeaders.findIndex(
      header => header.columnTitle.toLowerCase() === columnName.toLowerCase(),
    );

    if (foundIndex != -1) {
      const dynamicHeaders = this.superTableHeaderComponents
        .toArray()
        .filter(header => header.canResize === true);
      const currentResizedHeaderWidth: number =
        dynamicHeaders.find(
          s => s.columnName.toLowerCase() === columnName.toLowerCase(),
        )?.width || 0;

      const currentMinWidth: number | null =
        this.columnHeaders[foundIndex].minWidth;
      this.columnHeaders[foundIndex].currentWidth =
        currentMinWidth != null
          ? Math.max(currentResizedHeaderWidth, currentMinWidth)
          : currentResizedHeaderWidth;
      await this.tableColumnStore.setWidth(
        this.name,
        this.columnHeaders[foundIndex].columnTitle,
        currentResizedHeaderWidth,
      );
      this.setStickyValues();

      this.emitEnabledArrays();
    }
  }

  @Output()
  readonly rowsSelectedChange = this.doSelect.pipe(
    withLatestFrom(this.data, this.rowsSelectedMap, this.selectionKey),
    map(([select, dataContainer, selected, key]) => {
      if (select == null || !dataContainer || !dataContainer.data) {
        return []; //Deselect everything
      }
      const { data } = dataContainer;
      if (select.value == null) {
        return select.selected
          ? [
              ...new Set([
                ...data.filter(x => {
                  if (!(key == 'id' && +x[key] < 0)) {
                    return x;
                  }
                }),
                ...Array.from(selected.values()),
              ]),
            ]
          : this.removeData(selected, data, key);
      }
      let newSelectedArray;
      if (select.selected) {
        if (this.lastSelectedItem && this.isShiftPressed) {
          // Calculate the range between select.value and lastSelectedItem
          const startIndex = data.findIndex(
            item => item[key] === this.lastSelectedItem[key],
          );
          const endIndex = data.findIndex(
            item => item[key] === select.value[key],
          );
          const range = [startIndex, endIndex].sort((a, b) => a - b);

          // Include all items within the range
          const rangeItems = data
            .slice(range[0], range[1] + 1)
            .filter(item => !(key == 'id' && +item[key] < 0));

          newSelectedArray = [
            ...new Set([...Array.from(selected.values()), ...rangeItems]),
          ];
        } else {
          newSelectedArray = [...Array.from(selected.values()), select.value];
        }
        this.lastSelectedItem = select.value;
      } else {
        newSelectedArray = [...Array.from(selected.values())].filter(
          sel => sel[key] != select.value[key],
        );
      }
      return newSelectedArray;
    }),
  );

  lastSelectedItem: any = null;
  isShiftPressed: boolean = false;
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.isShiftPressed = true;
    }
  }
  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Shift') {
      this.isShiftPressed = false;
    }
  }

  @Output()
  readonly sortChange = merge(this.sort, this.changeSort);

  @Output()
  readonly viewChange = this.view.pipe(
    switchMap(view =>
      this.changeView.pipe(map(change => ({ ...view, ...change }))),
    ),
  );

  @Output()
  dataArray = new EventEmitter<Observable<Result<T, null>>>();

  @Output()
  enabledColumnArray = new EventEmitter<SuperColumn[]>();

  @Output()
  enabledPinnedArray = new EventEmitter<SuperColumn[]>();

  @Output()
  enabledUnpinnedArray = new EventEmitter<SuperColumn[]>();

  @Output()
  dataChange = new EventEmitter<Result<T>>();

  constructor(
    private tableColumnStore: TableColumnStore,
    private changeService: ChangeService,
    @Optional() private readonly analyticsService: AccountAnalyticsService,
  ) {
    if (this.customizeIndexesThrough) {
      this.tableColumnStore.setCustomizeIndexesThrough(
        this.customizeIndexesThrough,
      );
    }
  }

  async ngAfterViewInit() {
    await this.tableColumnStore.updateTableHeaderList(
      this.name,
      this.columnHeaders,
    );
    await this.tableColumnStore.setTableHeaderConstants(
      this.name,
      this.columnHeaders,
    );

    this.scrollPosition.next(this.scroller.nativeElement);

    await this.updateOrSetColumnDropdownOptions();

    await this.updateColumnHeadersFromSettings();
    this.setStickyValues();

    this.changeService.listen(this.selectedColumns, async selected => {
      this.columnsSelected = selected.length !== 0;
      await this.tableColumnStore.setEnabled(this.name, selected);
      await this.updateColumnHeadersFromSettings();
      this.setStickyValues();
      this.emitEnabledArrays();
      this.columnsLoaded = true;
    });

    this.refreshPersistedColumns.next(undefined);

    this.changeService.listen(this.data, data => {
      if (data != null) {
        this.dataChange.emit(data);
      }
      //Data changed, deselect all selections
      //We tried going with removing rows that are selected and don't exist in the data.
      //However, in denials and other places we have multiple supertables sharing a selected list.
      //These super tables don't always have the selected row in them. So, deselecting
      //rows that don't exist in the table is not a currently valid way to make sure all the selected
      //rows exist. Instead on any data changes, deselect all rows. RIVET-17423
      if (!this.overrideDefaultDeselection) {
        this.doSelect.next(null);
      }
      // Underpayments built its own deselection when filters change, so we are overriding
      // this defualt deselection on data change so that deselection doesn't occur
      // when users change the page number on the underpayments table. RIVET-17663
    });
  }

  changeSortSideEffects = this.changeSort
    .pipe(
      tap((order: Order) => {
        if (!this.analyticsService) return;
        this.analyticsService.logEvent(SortTable, {
          // Ideally, we would have canonical names for tables that get used for
          // analytics here as well as on <search-field> and <export-csv>.
          // However, most of the codebase does not supply tableName or
          // columnName to super-table-header.
          'table name': '(Unknown)',
          'sort by':
            order.name === 'default'
              ? null
              : sentenceCaseFromCamelCase(order.name),
          direction:
            order.name === 'default'
              ? null
              : {
                  asc: 'Ascending' as const,
                  desc: 'Descending' as const,
                }[order.direction],
        });
      }),
    )
    .subscribe();
}
